import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, ArrowRight } from 'lucide-react';
import { DjayAvatar } from '../../../mascot';
import { type CelebrationTriggers } from '../../hooks/useSessionExperience';
import { DjayGrowthState } from '../../../components/shared/DjayGrowthState';
import { FAMILY_DISPLAY_NAMES } from '../../../content/familyLabels';
import { hapticManager } from '../../../utils/hapticManager';
import { SummaryShell } from './SummaryShell';
interface CelebrationMomentsProps {
  triggers: CelebrationTriggers;
  onComplete: () => void;
}
type MomentType = 'foutloos' | 'record' | 'stageUp' | 'streak';
export function CelebrationMoments({
  triggers,
  onComplete
}: CelebrationMomentsProps) {
  const [moments, setMoments] = useState<MomentType[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const queue: MomentType[] = [];
    if (triggers.foutloos) queue.push('foutloos');
    if (triggers.recordBroken) queue.push('record');
    if (triggers.stageUpgrades.length > 0) queue.push('stageUp');
    if (triggers.streakMilestone) queue.push('streak');
    setMoments(queue.slice(0, 3)); // hard cap
  }, [triggers]);
  useEffect(() => {
    if (moments.length === 0) {
      onComplete();
      return;
    }
    if (currentIndex >= moments.length) {
      onComplete();
      return;
    }
    const timer = setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, 1800);
    return () => clearTimeout(timer);
  }, [currentIndex, moments, onComplete]);
  const handleSkip = () => {
    hapticManager.hapticSelection();
    onComplete();
  };
  if (moments.length === 0 || currentIndex >= moments.length) return null;
  const currentMoment = moments[currentIndex];
  return <SummaryShell tone="celebrated" cornerSlot={<button type="button" onClick={handleSkip} aria-label="Door naar overzicht" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-md backdrop-blur transition-transform hover:scale-105 active:scale-95" data-id="element-12252" data-mp="1loc6va8im5y9">
          <ArrowRight className="h-5 w-5" data-id="element-12253" data-mp="3fz277xxhggjs" />
        </button>} data-id="element-12251" data-mp="1loveny4gbdyk">
      {/* Whole-area click to skip (excluding the corner slot) */}
      <button type="button" onClick={handleSkip} aria-label="Tik om verder te gaan" className="absolute inset-0 cursor-pointer" data-id="element-12254" data-mp="366mp90m6rryw" />

      <div className="relative flex flex-col items-center text-center" data-id="element-12255" data-mp="3tuxlfqgxt3gu">
        {/* Progress dots */}
        <div className="mb-8 flex gap-1.5" data-id="element-12256" data-mp="2o374qx822ht">
          {moments.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full transition-all ${idx <= currentIndex ? 'w-8 bg-amber-400' : 'w-6 bg-slate-200'}`} data-id="element-12257" data-mp="3ky53asvhjcfm" data-mpi={idx} />)}
        </div>

        <AnimatePresence mode="wait" data-id="element-12258" data-mp="29j9pf94nxf04">
          <motion.div key={currentMoment} initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 1.02,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center" data-id="element-12259" data-mp="2diqwu83ngm7x">
            {currentMoment === 'foutloos' && <>
                <DjayAvatar emotion="cheering" size="lg" className="mb-6" data-id="element-12260" data-mp="1i9n7vpjfwkux" />
                <div className="mb-5 flex gap-1.5" data-id="element-12261" data-mp="1i7kc0ovlvxia">
                  {Array.from({
                length: 5
              }).map((_, i) => <motion.div key={i} initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: i * 0.08,
                type: 'spring'
              }} className="h-5 w-5 rounded-full bg-emerald-500 ring-4 ring-emerald-200" data-id="element-12262" data-mp="1s6t0x3bm6lxs" data-mpi={i} />)}
                </div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-12263" data-mp="bqdr6aex3vra">Foutloos!</h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-12264" data-mp="gk8c1h69egry">
                  Alles in één keer goed.
                </p>
              </>}

            {currentMoment === 'record' && <>
                <DjayAvatar emotion="celebrating" size="lg" className="mb-6" data-id="element-12265" data-mp="uiorbwi6tflp" />
                <motion.div initial={{
              scale: 0,
              rotate: -10
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              bounce: 0.5
            }} className="mb-5 rounded-full bg-amber-100 p-5" data-id="element-12266" data-mp="uglr6jnbm639">
                  <Trophy className="h-14 w-14 text-amber-500" data-id="element-12267" data-mp="2oxvkjz98bzu2" />
                </motion.div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-12268" data-mp="2jz2726ty08tw">
                  Nieuw record!
                </h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-12269" data-mp="2f21x3ktz2lxi">
                  Sneller of beter dan ooit.
                </p>
              </>}

            {currentMoment === 'stageUp' && triggers.stageUpgrades[0] !== undefined && <>
                  <DjayAvatar emotion="proud" size="lg" className="mb-6" data-id="element-12270" data-mp="32pa4c8ayo526" />
                  <div className="mb-5 flex items-center gap-3" data-id="element-12271" data-mp="2yozvmnvsb0jm">
                    <DjayGrowthState level={triggers.stageUpgrades[0].oldStage.level} size="lg" className="opacity-50 grayscale" data-id="element-12272" data-mp="2yqsuhh05ogh2" />
                    <motion.div initial={{
                x: -10,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 0.3
              }} data-id="element-12273" data-mp="1oqqf93izad9a">
                      <Star className="h-7 w-7 text-emerald-400" data-id="element-12274" data-mp="1608rzegx09vb" />
                    </motion.div>
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.5,
                type: 'spring'
              }} data-id="element-12275" data-mp="1aqyvsn9p04nw">
                      <DjayGrowthState level={triggers.stageUpgrades[0].newStage.level} size="lg" data-id="element-12276" data-mp="n3h9v8d400su" />
                    </motion.div>
                  </div>
                  <h2 className="text-3xl font-bold text-slate-800" data-id="element-12277" data-mp="3fuks8viprax">
                    Niveau omhoog!
                  </h2>
                  <p className="mt-1.5 text-base text-slate-600" data-id="element-12278" data-mp="hfm32lt2htuq">
                    {FAMILY_DISPLAY_NAMES[triggers.stageUpgrades[0].familyId] ?? 'Deze vragen'}{' '}
                    gaan steeds beter.
                  </p>
                </>}

            {currentMoment === 'streak' && <>
                <DjayAvatar emotion="celebrating" size="lg" className="mb-6" data-id="element-12279" data-mp="mcckpdkisxi9" />
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1.1
            }} transition={{
              type: 'spring',
              bounce: 0.6
            }} className="mb-5 rounded-full bg-orange-100 p-5" data-id="element-12280" data-mp="105c1diw1gxre">
                  <Flame className="h-14 w-14 text-orange-500" data-id="element-12281" data-mp="2cgug2b1hxf2x" />
                </motion.div>
                <h2 className="text-3xl font-bold text-slate-800" data-id="element-12282" data-mp="2gho8nhpvvi25">
                  {triggers.streakMilestone} dagen!
                </h2>
                <p className="mt-1.5 text-base text-slate-600" data-id="element-12283" data-mp="2qh599kqa35r9">
                  Wat een doorzettingsvermogen.
                </p>
              </>}
          </motion.div>
        </AnimatePresence>

        <p className="mt-10 text-xs text-slate-400" data-id="element-12284" data-mp="1xs83gh3971fe">Tik om verder te gaan</p>
      </div>
    </SummaryShell>;
}